import { useState } from "react";
import { supabase } from "../lib/supabaseClient.js";

interface AddPestWeatherAlertModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  farms: { id: string; name: string }[];
  onAlertAdded: () => void;
}

export default function AddPestWeatherAlertModal({
  open,
  onClose,
  userId,
  farms,
  onAlertAdded,
}: AddPestWeatherAlertModalProps) {
  const [type, setType] = useState<"pest" | "weather">("pest");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [alertLevel, setAlertLevel] = useState("info");
  const [farmId, setFarmId] = useState<string | "">("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const { error: insertError } = await supabase.from("pest_weather_alerts").insert([
      {
        user_id: userId,
        type,
        title,
        description,
        alert_level: alertLevel,
        farm_id: farmId || null,
      },
    ]);
    setLoading(false);

    if (insertError) {
      setError(insertError.message);
      return;
    }
    // Reset
    setType("pest");
    setTitle("");
    setDescription("");
    setAlertLevel("info");
    setFarmId("");
    onAlertAdded?.();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="relative w-full max-w-md p-6 bg-white rounded shadow-md">
        <button
          className="absolute text-gray-400 top-3 right-3 hover:text-red-500"
          onClick={onClose}
        >
          ×
        </button>
        <h2 className="mb-4 text-xl font-bold">Add Pest/Weather Alert</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block mb-1 font-medium">Type</label>
            <select
              value={type}
              onChange={e => setType(e.target.value as "pest" | "weather")}
              className="w-full px-2 py-1 border rounded"
              required
            >
              <option value="pest">Pest</option>
              <option value="weather">Weather</option>
            </select>
          </div>
          <div>
            <label className="block mb-1 font-medium">Title</label>
            <input
              className="w-full px-2 py-1 border rounded"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
              maxLength={100}
            />
          </div>
          <div>
            <label className="block mb-1 font-medium">Description</label>
            <textarea
              className="w-full px-2 py-1 border rounded"
              value={description}
              onChange={e => setDescription(e.target.value)}
              maxLength={500}
              rows={3}
            />
          </div>
          <div>
            <label className="block mb-1 font-medium">Alert Level</label>
            <select
              value={alertLevel}
              onChange={e => setAlertLevel(e.target.value)}
              className="w-full px-2 py-1 border rounded"
              required
            >
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div>
            <label className="block mb-1 font-medium">Farm (optional)</label>
            <select
              value={farmId}
              onChange={e => setFarmId(e.target.value)}
              className="w-full px-2 py-1 border rounded"
            >
              <option value="">All Farms / General Alert</option>
              {farms.map(farm => (
                <option key={farm.id} value={farm.id}>
                  {farm.name}
                </option>
              ))}
            </select>
          </div>
          {error && <div className="text-red-600">{error}</div>}
          <div className="flex justify-end">
            <button
              type="submit"
              className="px-4 py-2 font-semibold text-white bg-green-700 rounded hover:bg-green-900"
              disabled={loading}
            >
              {loading ? "Adding..." : "Add Alert"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}